import React, { useEffect, useRef, useState } from "react";
import { Grid, GridCellProps, GridExpandChangeEvent, GridNoRecords, GridRowProps } from "@progress/kendo-react-grid";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { ChooserColumn, EmptyState, Icon, Spinner, toast, useKendoGrid, useKendoTree } from "@axum/ax-design";
import { BASE_URL } from "../../../../redux/actions/dashboard/env.variables";
import { customAxios } from "../../../../utils/customAxios";
import { objectIsNullOrEmpty } from "../../../../utils/functions";
import { buildKendoFilter } from "../../../../utils/kendoUtils";
import { orderDateParts } from "../../../../utils/orderDisplay";
import { useSellerStatsFailed } from "../../../../utils/loadErrors";
import { updateOrderState } from "../retry/fetchers";
import { orderToOrderIdentifier } from "../retry/interfaces";
import OrderBubble, { OrderBubbleContext } from "../../../Common/Orders/OrderBubble";
import { useOrderLogs } from "../../../Common/Orders/OrderLogs";
import { useConfirm } from "../../../Common/Orders/useConfirm";
import { OrderToolsProvider } from "../../../Common/Orders/OrderTools";
import { LEAD_WIDTH, ORDER_COLUMNS, renderOrderColumns, toneRow, TOOLS_WIDTH, useOrderColumns } from "../../../Common/Orders/orderColumns";
import SourcesBar from "./SourcesBar";
import { SellersGridContainer } from "./styles";

interface Props {
  tableData: any[];
  loading: boolean;
  requestNewPage: (skip?: number, take?: number, sort?: any) => void;
  take: number;
  skip: number;
  total: number;
  activeFilters: CompositeFilterDescriptor;
  featuresAllowed: string[];
}

interface DayOrders {
  loading: boolean;
  rows: any[];
}

const GROUP = [{ field: "seller" }, { field: "day" }];

const SELLER_COLUMNS = ORDER_COLUMNS.map(column => (column.key === "sellerName" ? { ...column, visible: false } : column));

// KendoReact 3 crea una columna de grupo por cada nivel abierto: sin esta franja que nunca se dibuja, el encabezado se corre al abrir un vendedor.
const DEPTH_KEEPER = {
  field: "seller",
  value: "depth-keeper",
  key: "depth-keeper",
  hidden: true,
  expanded: true,
  aggregates: {},
  items: [{ field: "day", value: "depth-keeper", key: "depth-keeper|day", hidden: true, expanded: false, aggregates: {}, items: [] }],
};

const sellerRow = (row: React.ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) =>
  rowProps.dataItem.hidden ? null : toneRow(row, rowProps);

const sellerKey = (seller: any) => String(seller.sellerId);
const dayKey = (seller: any, day: any) => `${seller.sellerId}|${day.date}`;

const dayFilter = (sellerId: any, activeFilters: CompositeFilterDescriptor): CompositeFilterDescriptor => {
  const bySeller = buildKendoFilter([{ values: [{ sellerId }], key: "sellerId" }]) as CompositeFilterDescriptor;
  return objectIsNullOrEmpty(activeFilters)
    ? bySeller
    : { ...activeFilters, filters: [...activeFilters.filters, ...bySeller.filters] };
};

const ClientsFigure = ({ count }: { count: number }) => (
  <span className="ax-table-node-figure"><b>{count}</b> {count === 1 ? "cliente" : "clientes"}</span>
);

const SellerBand = ({ seller }: { seller: any }) => {
  const last = orderDateParts(seller.lastOrderDate);
  const people = [
    seller.supervisorName && `Supervisor: ${seller.supervisorName}`,
    seller.leaderName && `Líder: ${seller.leaderName}`,
  ].filter(Boolean).join(" · ");
  return (
    <>
      <span className="ax-table-node-title">{seller.sellerName}</span>
      <span className="ax-table-node-note">{seller.sellerId}</span>
      {people && <span className="o360-band-people">{people}</span>}
      <span className="ax-table-node-meta">
        {last && <span className="ax-table-node-figure o360-last-order">Último pedido <b>{last.day} {last.time}</b></span>}
        <SourcesBar sources={seller.sourcePercentages} />
        <ClientsFigure count={seller.totalClients} />
      </span>
    </>
  );
};

const DayBand = ({ day, loading }: { day: any; loading: boolean }) => {
  const parts = orderDateParts(day.date);
  return (
    <>
      <span className="ax-table-node-title">{parts ? parts.day : day.date}</span>
      {parts && <span className="ax-table-node-note">{parts.relative}</span>}
      {loading && <Spinner size="sm" />}
      <span className="ax-table-node-meta">
        <SourcesBar sources={day.sourcePercentages} />
        <ClientsFigure count={day.totalClients} />
      </span>
    </>
  );
};

// KendoReact dibuja la franja como <td><p class="k-reset"><a class="k-icon"/>valor</p></td>: se conserva el ícono y se cambia el valor.
const bandCell = (td: React.ReactElement | null, props: GridCellProps) => {
  if (!td || props.rowType !== "groupHeader" || !td.props.children) return td;
  const group = props.dataItem;
  const p = td.props.children;
  const band = group.field === "seller"
    ? <SellerBand seller={group.seller} />
    : <DayBand day={group.day} loading={group.loading} />;
  return React.cloneElement(td, td.props, React.cloneElement(p, p.props, p.props.children[0], band));
};

export default (props: Props) => {
  const host = useRef<HTMLDivElement>(null);
  useKendoTree(host);
  useKendoGrid(host);

  const [columns, setColumns] = useState<ChooserColumn[]>(SELLER_COLUMNS);
  const shown = useOrderColumns(host, columns, SELLER_COLUMNS, LEAD_WIDTH * (GROUP.length + 1) + TOOLS_WIDTH);
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [openOrders, setOpenOrders] = useState<number[]>([]);
  const [days, setDays] = useState<{ [key: string]: DayOrders }>({});
  const generation = useRef(0);
  const { showLogs, logsModal } = useOrderLogs();
  const { confirm, confirmDialog } = useConfirm();
  const failed = useSellerStatsFailed();

  useEffect(() => {
    generation.current++;
    setOpenGroups([]);
    setOpenOrders([]);
    setDays({});
  }, [props.tableData]);

  const sellers = props.tableData || [];
  const sellerKeys = sellers.map(sellerKey);
  const allExpanded = sellerKeys.length > 0 && sellerKeys.every(key => openGroups.includes(key));

  const loadDay = (seller: any, day: any) => {
    const key = dayKey(seller, day);
    const requested = generation.current;
    const date = new Date(day.date);
    const params = {
      query: {
        entity: "orders",
        year: date.getFullYear().toString(),
        month: (date.getMonth() + 1).toString(),
        days: date.getDate().toString(),
      },
      body: { filter: dayFilter(seller.sellerId, props.activeFilters) },
    };

    setDays(current => ({ ...current, [key]: { loading: true, rows: current[key] ? current[key].rows : [] } }));
    customAxios(BASE_URL)
      .post("/api/Orders", params)
      .then(res => {
        if (requested !== generation.current) return;
        setDays(current => ({ ...current, [key]: { loading: false, rows: res.data.data || [] } }));
      })
      .catch(error => {
        console.error(error);
        if (requested !== generation.current) return;
        toast.show("No se pudieron traer los pedidos del día", { type: "danger" });
        setDays(current => {
          const rest = { ...current };
          delete rest[key];
          return rest;
        });
        setOpenGroups(keys => keys.filter(k => k !== key));
      });
  };

  const reloadDay = (key: string) => sellers.forEach(seller =>
    (seller.sellerStatsByDates || []).forEach((day: any) => {
      if (dayKey(seller, day) === key) loadDay(seller, day);
    })
  );

  const sellerGroups = sellers.map(seller => ({
    field: "seller",
    value: sellerKey(seller),
    key: sellerKey(seller),
    aggregates: {},
    seller,
    expanded: openGroups.includes(sellerKey(seller)),
    items: (seller.sellerStatsByDates || []).map((day: any) => {
      const key = dayKey(seller, day);
      const orders = days[key];
      return {
        field: "day",
        value: key,
        key,
        aggregates: {},
        seller,
        day,
        loading: Boolean(orders && orders.loading),
        expanded: openGroups.includes(key),
        items: orders ? orders.rows.map(order => ({ ...order, dayKey: key, expanded: openOrders.includes(order.id) })) : [],
      };
    }),
  }));
  const data = sellerGroups.length > 0 ? [...sellerGroups, DEPTH_KEEPER] : sellerGroups;

  const toggle = (event: GridExpandChangeEvent) => {
    const item = event.dataItem;
    if (!item.items) {
      setOpenOrders(ids => (event.value ? [...ids, item.id] : ids.filter(id => id !== item.id)));
      return;
    }
    setOpenGroups(keys => (event.value ? [...keys, item.key] : keys.filter(k => k !== item.key)));
    if (event.value && item.field === "day" && !days[item.key]) loadDay(item.seller, item.day);
  };

  const expandAll = (open: boolean) => setOpenGroups(keys => {
    const inner = keys.filter(key => !sellerKeys.includes(key));
    return open ? [...inner, ...sellerKeys] : inner;
  });

  const updateState = (order: any, erpState: string) =>
    updateOrderState(erpState, [orderToOrderIdentifier(order)]).then(x => {
      if (x.notUpdated > 0) toast.show(x.error, { type: "danger" });
      else toast.show("Actualizado exitosamente", { type: "success" });
      reloadDay(order.dayKey);
    });

  return (
    <OrderBubbleContext.Provider value={{ featuresAllowed: props.featuresAllowed, showLogs, updateState, confirm }}>
      <OrderToolsProvider
        onRefresh={() => props.requestNewPage(props.skip, props.take)}
        loading={props.loading}
        expanded={allExpanded}
        onExpandAll={expandAll}
        columns={columns}
        onColumnsChange={setColumns}
        onColumnsRestore={() => setColumns(SELLER_COLUMNS)}
      >
        <SellersGridContainer ref={host}>
          <Grid
            className={`ax-kendo ax-kendo-tree${props.loading ? " is-loading" : ""}`}
            data={data}
            group={GROUP}
            detail={OrderBubble}
            expandField="expanded"
            onExpandChange={toggle}
            cellRender={bandCell}
            rowRender={sellerRow}
            scrollable="none"
            pageable
            take={props.take}
            skip={props.skip}
            total={props.total}
            onPageChange={event => props.requestNewPage(event.page.skip, event.page.take)}
          >
            <GridNoRecords>
              {failed && !props.loading ? (
                <EmptyState
                  icon={<Icon name="warning" />}
                  title="No se pudieron traer los vendedores"
                  description="Volvé a aplicar los filtros para reintentar."
                />
              ) : (
                <EmptyState
                  icon={props.loading ? <Spinner /> : <Icon name="inbox" />}
                  title={props.loading ? "Buscando vendedores…" : "No hay vendedores con pedidos"}
                  description={props.loading ? undefined : "Probá con otro rango de fechas o con menos filtros."}
                />
              )}
            </GridNoRecords>
            {renderOrderColumns(shown)}
          </Grid>
        </SellersGridContainer>
      </OrderToolsProvider>
      {logsModal}
      {confirmDialog}
    </OrderBubbleContext.Provider>
  );
};
